
import React from 'react';
import { useAuth } from '@/context/AuthContext';
import { Link } from 'react-router-dom';

const Dashboard: React.FC = () => {
  const { user } = useAuth();

  const stats = [
    { title: 'Workspaces', value: '12', icon: 'ri-folder-line', color: 'bg-blue-600', change: '+2 this week', link: '/workspaces' },
    { title: 'Data Stores', value: '34', icon: 'ri-database-line', color: 'bg-green-600', change: '+5 new stores', link: '/stores' },
    { title: 'Layers', value: '127', icon: 'ri-stack-line', color: 'bg-purple-600', change: '+12 published', link: '/layers' },
    { title: 'Styles', value: '89', icon: 'ri-palette-line', color: 'bg-orange-600', change: '+3 updated', link: '/styles' },
  ];

  const serviceStatus = [
    { name: 'WMS', status: 'active', uptime: '99.8%', requests: '15.2K', icon: 'ri-map-line' },
    { name: 'WFS', status: 'active', uptime: '99.5%', requests: '8.7K', icon: 'ri-file-list-line' },
    { name: 'WCS', status: 'active', uptime: '98.2%', requests: '2.1K', icon: 'ri-image-line' },
    { name: 'WMTS', status: 'maintenance', uptime: '95.1%', requests: '22.8K', icon: 'ri-grid-line' },
  ];

  const recentActivities = [
    { id: 1, action: 'New layer published', item: 'roads_2024', time: '5 minutes ago', icon: 'ri-stack-line', type: 'success' },
    { id: 2, action: 'Workspace created', item: 'city_planning', time: '12 minutes ago', icon: 'ri-folder-add-line', type: 'info' },
    { id: 3, action: 'Style updated', item: 'water_bodies', time: '25 minutes ago', icon: 'ri-brush-line', type: 'warning' },
    { id: 4, action: 'Cache cleared', item: 'satellite_imagery', time: '1 hour ago', icon: 'ri-refresh-line', type: 'info' },
    { id: 5, action: 'Service restarted', item: 'WMS Service', time: '2 hours ago', icon: 'ri-restart-line', type: 'error' },
  ];

  const quickActions = [
    { title: 'New Workspace', icon: 'ri-folder-add-line', color: 'bg-blue-600', link: '/workspaces' },
    { title: 'Add Data Store', icon: 'ri-database-2-line', color: 'bg-green-600', link: '/stores' },
    { title: 'Publish Layer', icon: 'ri-stack-line', color: 'bg-purple-600', link: '/layers' },
    { title: 'Create Style', icon: 'ri-brush-line', color: 'bg-orange-600', link: '/styles' },
    { title: 'Manage Cache', icon: 'ri-cached-line', color: 'bg-red-600', link: '/tile-caching' },
    { title: 'Server Settings', icon: 'ri-settings-3-line', color: 'bg-gray-600', link: '/settings' },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      case 'maintenance': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'error': return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
    }
  };

  const getActivityTypeColor = (type: string) => {
    switch (type) {
      case 'success': return 'bg-green-100 text-green-600 dark:bg-green-900/20 dark:text-green-400';
      case 'warning': return 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'error': return 'bg-red-100 text-red-600 dark:bg-red-900/20 dark:text-red-400';
      default: return 'bg-blue-100 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400';
    }
  };

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-green-600 to-blue-600 rounded-lg p-6 text-white">
        <h1 className="text-2xl font-bold mb-2">GeoServer Management Dashboard</h1>
        <p className="text-green-100">Welcome back, {user?.username}! Manage your geospatial data and services from here.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <Link 
            key={index} 
            to={stat.link}
            className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-all duration-200 cursor-pointer group"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{stat.title}</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1 group-hover:scale-105 transition-transform duration-200">{stat.value}</p>
                <p className="text-sm text-blue-600 dark:text-blue-400 mt-1">
                  {stat.change}
                </p>
              </div>
              <div className={`w-12 h-12 ${stat.color} rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-200`}>
                <i className={`${stat.icon} text-white text-xl`}></i>
              </div>
            </div>
          </Link>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Service Status */}
        <div className="xl:col-span-2 bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <i className="ri-server-line mr-2 text-blue-600"></i>
            Service Status
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {serviceStatus.map((service, index) => (
              <div key={index} className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center">
                    <i className={`${service.icon} text-lg text-gray-600 dark:text-gray-400 mr-2`}></i>
                    <span className="font-medium text-gray-900 dark:text-white">{service.name}</span>
                  </div>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(service.status)}`}>
                    {service.status}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Uptime:</span>
                    <span className="ml-1 font-medium text-gray-900 dark:text-white">{service.uptime}</span>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Requests:</span>
                    <span className="ml-1 font-medium text-gray-900 dark:text-white">{service.requests}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <i className="ri-history-line mr-2 text-green-600"></i>
            Recent Activity
          </h3>
          <div className="space-y-4">
            {recentActivities.map((activity) => (
              <div key={activity.id} className="flex items-start space-x-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${getActivityTypeColor(activity.type)}`}>
                  <i className={`${activity.icon} text-sm`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {activity.action}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {activity.item} • {activity.time}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <i className="ri-flashlight-line mr-2 text-purple-600"></i>
          Quick Actions
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {quickActions.map((action, index) => (
            <Link
              key={index}
              to={action.link}
              className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-all duration-200 text-center group hover:scale-105"
            >
              <div className={`w-12 h-12 ${action.color} rounded-lg flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-200`}>
                <i className={`${action.icon} text-white text-xl`}></i>
              </div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">{action.title}</p>
            </Link>
          ))}
        </div>
      </div>

      {/* System Health */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <i className="ri-pulse-line mr-2 text-red-600"></i>
          System Health
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-2">
              <i className="ri-cpu-line text-2xl text-green-600 dark:text-green-400"></i>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">CPU Usage</p>
            <p className="text-xl font-bold text-gray-900 dark:text-white">34%</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center mx-auto mb-2">
              <i className="ri-hard-drive-line text-2xl text-blue-600 dark:text-blue-400"></i>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Memory Usage</p>
            <p className="text-xl font-bold text-gray-900 dark:text-white">67%</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900/20 rounded-full flex items-center justify-center mx-auto mb-2">
              <i className="ri-database-line text-2xl text-purple-600 dark:text-purple-400"></i>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Disk Usage</p>
            <p className="text-xl font-bold text-gray-900 dark:text-white">45%</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
